import Config from '../../config';
import { FeedActions, getFeedActions } from './FeedState';
import { NotificationActions, QuoteDataActions } from '../global/actions';
import { FeedQuote, FeedSettlement, OpenPriceUpdate } from '@tradingblock/types';
import {
  isOpenPriceUpdate,
  isQuote,
  isSettlement,
  WebsocketFeedParser,
  isOCCSymbol,
  toRawOccSymbol,
} from '@tradingblock/api';
import _ from 'lodash';
import { batch } from 'react-redux';

// Quotes batching worker (initialized when PERF batching is enabled)
let quotesWorker: Worker | null = null;
let hasSetupQuotesWorkerListener = false;
// Stored interval from dashboard settings (used when creating worker)
let quotesWorkerIntervalMs: number | null = null;
// Last QuoteTime (as ms) sent to Redux per symbol - only dispatch if newer
const lastQuoteTimeSentToRedux = new Map<string, number>();

function toTimestamp(val: string | number | null | undefined): number {
  if (val == null) return 0;
  if (typeof val === 'number') return val;
  const parsed = new Date(val).getTime();
  return isNaN(parsed) ? 0 : parsed;
}

// JSON Channel message interfaces for quotes
interface JsonQuoteMessage {
  typ: 'q';
  ts: string;
  sym: string;
  lp?: number;
  b?: number;
  a?: number;
  c?: number;
  h?: number;
  l?: number;
  v?: number;
  p?: number;
  bz?: number;
  az?: number;
  is?: boolean;
  cp?: number;
  cp2?: number;
}

interface JsonSettlementMessage {
  typ: 's';
  ts: string;
  sym: string;
  cp2: number;
  sp: number;
}

interface JsonOpenPriceMessage {
  typ: 'o';
  ts: string;
  sym: string;
  op: number;
}

type JsonChannelMessage = JsonQuoteMessage | JsonSettlementMessage | JsonOpenPriceMessage;

// LoginCountWebSocketService.ts
class QuotesWebSocketService {
  private static instance: QuotesWebSocketService;
  private ws: WebSocket | null = null;
  private lastMessageTime: number = 0;
  private dataFlowCheckInterval: NodeJS.Timeout | null = null;
  private onDataFlowStopped: (() => void) | null = null;
  private hasSubscriptions: boolean = false;
  private readonly DATA_FLOW_TIMEOUT_MS = 30000; // 30 seconds without data = consider stopped

  private constructor() {}

  // Parse JSON channel message and convert to appropriate type
  private parseJsonChannelMessage(
    jsonData: string | object
  ): { type: 'quote' | 'settlement' | 'openPrice'; data: FeedQuote | FeedSettlement | OpenPriceUpdate } | null {
    try {
      let jsonMessage: JsonChannelMessage;

      if (typeof jsonData === 'string') {
        // Check if it's already a FIX format string (contains pipe separator)
        if (jsonData.includes('|')) {
          return null; // Let FIX parser handle it
        }
        // Try to parse as JSON
        try {
          jsonMessage = JSON.parse(jsonData);
        } catch (parseError) {
          return null; // Not valid JSON, let FIX parser handle it
        }
      } else {
        // Already an object, check if it looks like JSON (has typ field) vs FIX format
        if (jsonData && typeof jsonData === 'object' && 'typ' in jsonData) {
          jsonMessage = jsonData as JsonChannelMessage;
        } else {
          return null; // Doesn't look like JSON format, let FIX parser handle it
        }
      }

      // Validate required fields (allow empty strings, but not null/undefined)
      if (jsonMessage.ts == null || jsonMessage.sym == null || jsonMessage.typ == null) {
        return null;
      }

      // Process symbol (remove dots and handle OCC symbols, same as FIX parser)
      const symbol = jsonMessage.sym.replace(/\./g, '');
      const Symbol = isOCCSymbol(symbol) ? toRawOccSymbol(symbol) : symbol;

      // Handle different message types
      if (jsonMessage.typ === 'q') {
        const quote: FeedQuote & { ClosePrice?: number; ClosePriceTMinus2?: number; IsSettlementSet?: boolean } = {
          QuoteTime: jsonMessage.ts,
          Symbol,
          LastTradePrice: jsonMessage.lp != null ? jsonMessage.lp : 0,
          BidPrice: jsonMessage.b,
          AskPrice: jsonMessage.a,
          NetChange: jsonMessage.c != null ? jsonMessage.c : 0,
          High: jsonMessage.h != null ? jsonMessage.h : 0,
          Low: jsonMessage.l != null ? jsonMessage.l : 0,
          // Omit Volume when `v` is absent — defaulting to 0 was resetting cumulative baseline and caused
          // runaway chart volume on the next quote that included real cumulative `v`.
          ...(jsonMessage.v != null && Number.isFinite(jsonMessage.v) ? { Volume: jsonMessage.v } : {}),
          ChangePercentage: jsonMessage.p != null ? jsonMessage.p : 0,
          BidSize: jsonMessage.bz,
          AskSize: jsonMessage.az,
        };
        if (jsonMessage.cp != null) {
          quote.ClosePrice = jsonMessage.cp;
        }
        if (jsonMessage.cp2 != null) {
          quote.ClosePriceTMinus2 = jsonMessage.cp2;
        }
        if (jsonMessage.is != null) {
          quote.IsSettlementSet = jsonMessage.is;
        }
        return { type: 'quote', data: quote };
      } else if (jsonMessage.typ === 's') {
        // Settlement message - validate required fields
        if (jsonMessage.sp == null || jsonMessage.cp2 == null) {
          return null;
        }
        const settlement: FeedSettlement = {
          CloseDate: jsonMessage.ts,
          Symbol,
          ClosePrice: jsonMessage.sp,
          ClosePriceTMinus2: jsonMessage.cp2,
          IsSettlementSet: true,
        };
        return { type: 'settlement', data: settlement };
      } else if (jsonMessage.typ === 'o') {
        // Open Price message - validate required fields
        if (jsonMessage.op == null) {
          return null;
        }
        const openPrice: OpenPriceUpdate = {
          quoteTime: jsonMessage.ts,
          symbol: Symbol,
          openPrice: jsonMessage.op,
        };
        return { type: 'openPrice', data: openPrice };
      }

      return null;
    } catch (error) {
      // If JSON parsing fails, return null to fall back to FIX parser
      return null;
    }
  }

  // Singleton instance access
  public static getInstance(): QuotesWebSocketService {
    if (!QuotesWebSocketService.instance) {
      QuotesWebSocketService.instance = new QuotesWebSocketService();
      console.log('new quotes websocket instance created');
    }
    return QuotesWebSocketService.instance;
  }

  // Set callback for when data flow stops
  public setDataFlowStoppedCallback(callback: (() => void) | null) {
    this.onDataFlowStopped = callback;
  }

  // Set whether subscriptions exist (only monitor when subscriptions are active)
  public setHasSubscriptions(hasSubscriptions: boolean) {
    this.hasSubscriptions = hasSubscriptions;
    if (hasSubscriptions && this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Start monitoring if websocket is open and we have subscriptions
      this.startDataFlowMonitoring();
    } else if (!hasSubscriptions) {
      // Stop monitoring if no subscriptions
      this.stopDataFlowMonitoring();
    }
  }

  // Start monitoring data flow (only if subscriptions exist)
  private startDataFlowMonitoring() {
    // Don't start monitoring if there are no subscriptions
    if (!this.hasSubscriptions) {
      return;
    }

    if (this.dataFlowCheckInterval) {
      clearInterval(this.dataFlowCheckInterval);
    }
    this.lastMessageTime = Date.now();
    this.dataFlowCheckInterval = setInterval(() => {
      // Double-check subscriptions before triggering fallback
      if (!this.hasSubscriptions) {
        this.stopDataFlowMonitoring();
        return;
      }
      const timeSinceLastMessage = Date.now() - this.lastMessageTime;
      if (timeSinceLastMessage > this.DATA_FLOW_TIMEOUT_MS && this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.warn('Quotes WebSocket: No data received for', timeSinceLastMessage, 'ms, triggering fallback');
        if (this.onDataFlowStopped) {
          this.onDataFlowStopped();
        }
      }
    }, 5000); // Check every 5 seconds
  }

  // Stop monitoring data flow
  private stopDataFlowMonitoring() {
    if (this.dataFlowCheckInterval) {
      clearInterval(this.dataFlowCheckInterval);
      this.dataFlowCheckInterval = null;
    }
  }

  // Initialize WebSocket connection
  public async connect(
    dispatch: React.Dispatch<FeedActions>,
    quotesFeedDispatcher: any,
    fullDispatcher: any,
    globalDispatch: any,
    apiToken: string
  ) {
    try {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
        this.ws = new WebSocket(Config.tradingApiWebsocketsNew + '/quotes?token=' + apiToken + '&format=JSON');

        this.ws.onopen = () => {
          quotesFeedDispatcher.connected();
          getFeedActions(dispatch).setQuotesFeedStatus('open');
          this.lastMessageTime = Date.now();
          // Only start monitoring if subscriptions exist
          if (this.hasSubscriptions) {
            this.startDataFlowMonitoring();
          }
          if (Config.perf && Config.perf.batchedQuotes && typeof Worker !== 'undefined') {
            if (!quotesWorker) {
              quotesWorker = new Worker(`${process.env.PUBLIC_URL}/quotesWorker.js`);
              // Use stored interval from dashboard settings if available, otherwise fall back to config
              const intervalMs =
                quotesWorkerIntervalMs !== null ? quotesWorkerIntervalMs : Config.perf.quotesBatchIntervalMs;
              quotesWorker.postMessage({ type: 'CONFIG', intervalMs });
            }
            if (quotesWorker && !hasSetupQuotesWorkerListener) {
              quotesWorker.onmessage = (ev: MessageEvent) => {
                const data = ev.data as any;
                if (data && data.type === 'QUOTE_DELTAS' && data.quotes) {
                  const quotes = data.quotes;
                  const filtered: Record<string, FeedQuote> = {};
                  for (const symbol of Object.keys(quotes)) {
                    const quote = quotes[symbol];
                    const quoteTs = toTimestamp(quote.QuoteTime);
                    const lastTs = lastQuoteTimeSentToRedux.get(symbol);
                    const lastTsVal = lastTs != null ? lastTs : 0;
                    if (quoteTs > lastTsVal) {
                      filtered[symbol] = quote;
                      lastQuoteTimeSentToRedux.set(symbol, quoteTs);
                    }
                  }
                  if (Object.keys(filtered).length > 0) {
                    batch(() => fullDispatcher.feed.updateQuotes(filtered));
                  }
                }
              };
              hasSetupQuotesWorkerListener = true;
            }
          }
          globalDispatch(
            NotificationActions.hide({
              status: 'Error',
              title: 'Quotes WebSocket Error',
              message: '',
              global: false,
              time: new Date(),
              key: 'quotes-feed-error',
              read: false,
              hide: false,
              viewed: false,
            })
          );
          globalDispatch(
            NotificationActions.read({
              status: 'Error',
              title: 'Quotes WebSocket Error',
              message: '',
              global: false,
              time: new Date(),
              key: 'quotes-feed-error',
              read: false,
              hide: false,
              viewed: false,
            })
          );
        };

        this.ws.onmessage = (ev: MessageEvent) => {
          const val = ev.data;

          // Update last message time for data flow monitoring
          this.lastMessageTime = Date.now();

          // Try to parse as JSON first
          const jsonResult = this.parseJsonChannelMessage(val);

          if (jsonResult !== null) {
            // Use JSON object directly
            const rawValue = typeof val === 'string' ? val : JSON.stringify(val);

            if (jsonResult.type === 'quote') {
              const quote = jsonResult.data as FeedQuote;
              if (Config.perf && Config.perf.batchedQuotes && quotesWorker) {
                quotesWorker.postMessage({ type: 'QUOTE', quote: quote });
              } else {
                // Add to FeedState queue for FeedSync processing
                getFeedActions(dispatch).onQuote(quote, rawValue);
              }
            } else if (jsonResult.type === 'settlement') {
              const settlement = jsonResult.data as FeedSettlement;
              getFeedActions(dispatch).onSettlement(settlement, rawValue);
              // When batched quotes is enabled, FeedSync is disabled - settlement must be
              // dispatched directly to quote reducer or it never reaches global state
              if (Config.perf && Config.perf.batchedQuotes && fullDispatcher) {
                batch(() =>
                  fullDispatcher.feed.updateQuotes({
                    [settlement.Symbol]: {
                      ...settlement,
                      LastTradePrice: settlement.ClosePrice,
                    } as unknown as FeedQuote,
                  })
                );
              }
            } else if (jsonResult.type === 'openPrice') {
              const openPrice = jsonResult.data as OpenPriceUpdate;
              globalDispatch(QuoteDataActions.receiveOpenPrice(openPrice));
            }
          } else {
            // Fall back to FIX format parser (original implementation)
            const parser = new WebsocketFeedParser(val);
            const msg = parser.parse();

            if (_.isNil(msg)) {
              console.warn('unknown feed type', val);
            } else if (!msg.isValid) {
              console.warn('errored feed message', val);
            } else if (isQuote(msg)) {
              if (Config.perf && Config.perf.batchedQuotes && quotesWorker) {
                quotesWorker.postMessage({ type: 'QUOTE', quote: msg });
              } else {
                // Add to FeedState queue for FeedSync processing
                getFeedActions(dispatch).onQuote(msg, val);
              }
            } else if (isSettlement(msg)) {
              const settlement = msg as FeedSettlement;
              getFeedActions(dispatch).onSettlement(settlement, val);
              // When batched quotes is enabled, FeedSync is disabled - settlement must be
              // dispatched directly to quote reducer or it never reaches global state
              if (Config.perf && Config.perf.batchedQuotes && fullDispatcher) {
                batch(() =>
                  fullDispatcher.feed.updateQuotes({
                    [settlement.Symbol]: {
                      ...settlement,
                      LastTradePrice: settlement.ClosePrice,
                    } as unknown as FeedQuote,
                  })
                );
              }
            } else if (isOpenPriceUpdate(msg)) {
              globalDispatch(QuoteDataActions.receiveOpenPrice(msg));
            }
          }
        };

        this.ws.onclose = (ev: CloseEvent) => {
          this.stopDataFlowMonitoring();
          if (ev.reason) {
            getFeedActions(dispatch).setQuotesCloseReason(ev.reason);
            console.warn(ev.reason);
          }
          getFeedActions(dispatch).setQuotesFeedStatus('closed');
          // Notification will be handled by initializer based on both websockets status
        };

        this.ws.onerror = error => {
          this.stopDataFlowMonitoring();
          quotesFeedDispatcher.errored({ error: error, reason: 'Unable to connect', message: 'Datafeed error' });
          getFeedActions(dispatch).setQuotesFeedStatus('error');
          console.error('Quotes WebSocket error:', error);
          // Notification will be handled by initializer based on both websockets status
        };
      }
    } catch (createWebsocketError) {
      quotesFeedDispatcher.errored({
        error: createWebsocketError,
        reason: 'Error creating quotes websocket',
        message: 'Quotes Websocket error',
      });
    }
  }
  // Send message to WebSocket
  public sendMessage(data: string | ArrayBufferLike | Blob | ArrayBufferView) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    } else if (this.ws) {
      console.warn('trying to send message to closed/closing websocket', data);
    }
  }

  public shouldTryReconnect = () => {
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
      return true;
    }
    return false;
  };

  // Close WebSocket connection
  public closeConnection() {
    this.stopDataFlowMonitoring();
    if (this.ws) {
      this.ws.close();
    }
  }

  // Get last message time for monitoring
  public getLastMessageTime(): number {
    return this.lastMessageTime;
  }

  // Update the worker's flush interval from dashboard settings
  public updateWorkerInterval(intervalMs: number): void {
    if (typeof intervalMs === 'number' && intervalMs > 0) {
      // Store the interval so it's used when worker is created
      quotesWorkerIntervalMs = intervalMs;
      // Update the worker if it exists
      if (quotesWorker) {
        quotesWorker.postMessage({ type: 'CONFIG', intervalMs });
      }
    }
  }
}

export const quotesWebSocketService = QuotesWebSocketService.getInstance();
